// Users › Internal staff (wave 3, W2_Portal_Tables look): marketing, call
// centre, finance and scout accounts in the modern table, with Add and Edit
// in a dialog (one label above each field, errors under it, a busy Save)
// instead of material-table's inline rows, the role changed from its chip,
// and Delete after a question that names the person.
//
// Writes, exactly as before (memory: portal-full-record-overwrite):
// - Add (critical edits on): editUser(null, { ...newRow, usertype (default
//   callcenteragent), approved (default true) }) — mints a push key, no
//   welcome SMS — then fetchUsersOnce(). The new row starts as material-table
//   started it: firstName '', lastName '', approved true,
//   canManageNotifications false, plus what was filled in.
// - Edit (critical edits on): editUser(id, { ...row, ...edited }, row) — the
//   WHOLE record with the fields the person changed, and the row as loaded;
//   editUser writes only what differs — then fetchUsersOnce(). A refusal
//   keeps the dialog open with the reason.
// - The role chip, the approval switch and the push-notification switch write
//   ONLY their own field (updateUserFields), with critical edits on; the role
//   can never become admin here.
// - Delete (critical edits on): deleteUser(id) then fetchUsersOnce().
// Phone and email are hidden in demo mode; the CSV needs critical edits on.
// Call-centre feedback (29 Oct 2026): a copy button beside each number copies
// it without spaces (+251964540448) for MicroSIP; it never opens the row and
// is not there while numbers are hidden.
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { api } from 'common';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import { tokens as c } from '../theme';
import { NOTIFICATION_CAPABILITY } from '../common/notificationSecurity';
import { showToast, confirmDialog } from '../components/feedback';
import {
  CopyButton,
  DataTable,
  RecordDialog,
  PageHeader,
  StatusChip,
  YesNo,
  calmCaps,
  formatPhoneDisplay,
  phoneMatches,
  toEthiopianE164,
  usePageState,
} from '../components/modern';

// Define internal staff user types
const internalUserTypes = ['marketing', 'callcentersupervisor', 'callcenteragent', 'finance', 'scout'];

// All available user types for dropdown (excluding admin)
const availableUserTypes = ['marketing', 'callcentersupervisor', 'callcenteragent', 'finance', 'scout', 'customer', 'driver', 'fleetadmin'];

const ROLE_TONE = { marketing: 'accent', callcentersupervisor: 'info', callcenteragent: 'info', finance: 'success', scout: 'amber' };

const nameOf = (u) => `${(u && u.firstName) || ''} ${(u && u.lastName) || ''}`.trim();
const initialsOf = (u) =>
  [u && u.firstName, u && u.lastName]
    .map((n) => String(n || '').trim().charAt(0))
    .join('')
    .toUpperCase();

// The fields the edit dialog may change, and its on/off ones.
const EDIT_FIELDS = ['firstName', 'lastName', 'usertype', 'approved', NOTIFICATION_CAPABILITY];
const SWITCH_FIELDS = ['approved', NOTIFICATION_CAPABILITY];

// material-table's newData: the row as loaded with only the fields the
// person changed replaced. A field left alone keeps its stored value exactly
// (an unset switch is not turned into `false`, a name is not re-trimmed).
const mergeEdits = (row, values) => {
  const out = { ...row };
  EDIT_FIELDS.forEach((name) => {
    if (!Object.prototype.hasOwnProperty.call(values, name)) return;
    const before = row[name];
    const after = values[name];
    const same = SWITCH_FIELDS.includes(name)
      ? !!before === !!after
      : String(typeof before === 'string' ? before.trim() : before === undefined || before === null ? '' : before) ===
        String(after === undefined || after === null ? '' : after);
    if (!same) out[name] = after;
  });
  return out;
};

export default function InternalStaff() {
  const { t, i18n } = useTranslation();
  const settings = useSelector((state) => state.settingsdata.settings);
  const { editUser, deleteUser, fetchUsersOnce, updateUserFields } = api;
  const staticusers = useSelector((state) => state.usersdata.staticusers);
  const usersLoading = useSelector((state) => state.usersdata.loading);
  const usersError = useSelector((state) => state.usersdata.error);
  const dispatch = useDispatch();
  const { state } = useLocation();
  // The page and the table's search are kept while staff open a record and
  // come back (owner, 28 Oct); components/modern/pageState.js.
  const [page, setPage] = usePageState('internal_staff', 'page', Number.isInteger(state?.pageNo) && state.pageNo >= 0 ? state.pageNo : 0);
  const [editingUser, setEditingUser] = useState(null); // the row whose role menu is open
  const [editing, setEditing] = useState(null); // {} = add, { row } = edit

  useEffect(() => {
    if (Number.isInteger(state?.pageNo) && state.pageNo >= 0) setPage(state.pageNo);
  }, [state]);

  useEffect(() => {
    dispatch(fetchUsersOnce());
  }, [dispatch, fetchUsersOnce]);

  // Filter users to show only internal staff types
  const data = useMemo(() => (staticusers ? staticusers.filter((user) => internalUserTypes.includes(user.usertype)) : []), [staticusers]);
  const listFailed = !staticusers && !usersLoading && !!(usersError && usersError.flag);

  const critical = !!settings.AllowCriticalEditsAdmin;
  const L = (key, dflt, opts) => t(key, { defaultValue: dflt, ...(opts || {}) });
  const hiddenText = t('hidden_demo');
  const phoneWord = t('phone', { defaultValue: 'Phone number' });
  const yesText = L('yes', 'Yes');
  const noText = L('no', 'No');
  const roleLabel = (type) => t(type) || type;
  const saveFailed = () =>
    t('edit_user_save_failed', {
      defaultValue: 'The changes were not saved. Reload the page and try again. If it still fails, your role cannot make this change.',
    });

  const handleUserTypeChange = (userId, newUserType) => {
    const user = data.find((u) => u.id === userId);
    if (user && newUserType !== 'admin') {
      // Only the role changes — never replace the whole profile from a cached row.
      dispatch(updateUserFields(userId, { usertype: newUserType }));
      setEditingUser(null);
    }
  };

  const handleDeleteUser = async (rowData) => {
    if (!critical) return;
    const name = nameOf(rowData) || (critical ? formatPhoneDisplay(rowData.mobile) : '');
    const ok = await confirmDialog({
      title: name ? L('staff_delete_title', 'Delete {{name}}?', { name }) : t('confirm_delete_user'),
      body: L('staff_delete_body', "This removes the staff member's account and their access to the portal. It cannot be undone."),
      confirmLabel: t('delete'),
      // Not the pack's 'cancel': in Amharic and Tigrinya it is the same word
      // as 'delete' (ሰርዝ), which would label both buttons alike.
      cancelLabel: t('confirm_cancel', { defaultValue: 'Cancel' }),
      destructive: true,
    });
    if (ok) {
      dispatch(deleteUser(rowData.id));
      dispatch(fetchUsersOnce());
    }
  };

  // Grant/revoke the push-notification capability for one staff member.
  // Additive flag — it never changes the person's usertype.
  const handleNotificationAccess = (rowData) => {
    const patch = { [NOTIFICATION_CAPABILITY]: !(rowData[NOTIFICATION_CAPABILITY] === true) };
    dispatch(updateUserFields(rowData.id, patch));
    return { ...rowData, ...patch };
  };

  const handelApproved = (rowData) => {
    const patch = { approved: !rowData.approved };
    dispatch(updateUserFields(rowData.id, patch));
    return { ...rowData, ...patch };
  };

  // Add and Edit: the same editUser calls as the old inline rows.
  const handleSave = async (values, { mode, initialValues }) => {
    if (mode === 'add') {
      // Set default usertype to callcenteragent if not specified
      const userData = {
        ...values,
        usertype: values.usertype || 'callcenteragent',
        approved: values.approved !== undefined ? values.approved : true,
      };
      const saved = dispatch(editUser(null, userData)); // editUser mints a push key when id is null (no welcome SMS for staff)
      dispatch(fetchUsersOnce());
      const r = await saved;
      if (r && r.success === false) throw new Error(saveFailed());
      showToast.success(L('staff_added', 'Staff member added'));
      return;
    }
    // oldData = the row as it was loaded: editUser writes only the fields
    // this edit changed and never the cached row's server-owned money /
    // bonus fields (a stale echo rolls back newer balances, and the rules
    // refuse it).
    const newData = mergeEdits(initialValues, values);
    const saved = dispatch(editUser(newData.id, newData, initialValues));
    dispatch(fetchUsersOnce());
    const r = await saved;
    if (r && r.success === false) throw new Error(saveFailed());
    showToast.success(L('saved', 'Saved'));
  };

  const subText = { fontSize: 13, lineHeight: 1.45, color: c.INK_2 };
  const columns = [
    {
      id: 'person',
      title: L('name', 'Name'),
      field: 'firstName',
      sortValue: (row) => nameOf(row),
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 200 }}>
          {row.profile_image ? (
            <Avatar src={row.profile_image} alt="" sx={{ width: 36, height: 36 }} />
          ) : (
            <Avatar aria-hidden="true" sx={{ width: 36, height: 36, bgcolor: c.SURFACE_ALT, color: c.INK_2, fontSize: 14, fontWeight: 600 }}>
              {initialsOf(row) || '?'}
            </Avatar>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontSize: 15, fontWeight: 600, lineHeight: 1.4, color: c.INK, overflowWrap: 'anywhere' }}>{nameOf(row) || L('no_name', 'No name')}</Box>
            <Typography component="div" sx={{ ...subText, display: 'flex', alignItems: 'center', gap: 0.25, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
              {critical ? formatPhoneDisplay(row.mobile) : hiddenText}
              {critical ? <CopyButton value={row.mobile} phone what={phoneWord} sx={{ my: -0.5 }} /> : null}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      id: 'email',
      title: t('email'),
      field: 'email',
      sortValue: (row) => (critical ? row.email || '' : ''),
      render: (row) => (
        <Box component="span" sx={{ overflowWrap: 'anywhere' }}>
          {critical ? row.email || '' : hiddenText}
        </Box>
      ),
    },
    {
      id: 'usertype',
      title: t('user_type'),
      field: 'usertype',
      sortValue: (row) => roleLabel(row.usertype),
      searchValue: (row) => roleLabel(row.usertype),
      render: (row) => {
        if (editingUser === row.id) {
          return (
            <TextField
              select
              size="small"
              value={row.usertype}
              onChange={(e) => handleUserTypeChange(row.id, e.target.value)}
              SelectProps={{ defaultOpen: true, onClose: () => setEditingUser(null) }}
              inputProps={{ 'aria-label': `${t('user_type')}: ${nameOf(row)}` }}
              sx={{ minWidth: 200 }}
            >
              {availableUserTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {roleLabel(type)}
                </MenuItem>
              ))}
            </TextField>
          );
        }
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <StatusChip tone={ROLE_TONE[row.usertype] || 'neutral'} label={roleLabel(row.usertype)} />
            {critical ? (
              <Tooltip title={t('click_to_edit')}>
                <IconButton aria-label={`${t('click_to_edit')}: ${t('user_type')}, ${nameOf(row)}`} onClick={() => setEditingUser(row.id)}>
                  <EditOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>
        );
      },
    },
    { id: 'createdAt', title: t('createdAt'), field: 'createdAt', type: 'datetime', defaultSort: 'desc' },
    {
      id: 'approved',
      title: calmCaps(t('account_approve')),
      field: 'approved',
      sortValue: (row) => (row.approved ? 1 : 0),
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StatusChip
            tone={row.approved ? 'success' : 'neutral'}
            icon={row.approved ? <CheckRoundedIcon /> : <BlockRoundedIcon />}
            label={row.approved ? L('approved', 'Approved') : calmCaps(L('not_approved', 'Not approved'))}
          />
          <Switch
            size="small"
            disabled={!critical}
            checked={!!row.approved}
            onChange={() => handelApproved(row)}
            inputProps={{ 'aria-label': `${calmCaps(t('account_approve'))}: ${nameOf(row)}` }}
          />
        </Box>
      ),
    },
    {
      id: NOTIFICATION_CAPABILITY,
      title: t('notification_access', { defaultValue: 'Push Notifications' }),
      field: NOTIFICATION_CAPABILITY,
      sortValue: (row) => (row[NOTIFICATION_CAPABILITY] === true ? 1 : 0),
      searchable: false,
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <YesNo value={row[NOTIFICATION_CAPABILITY] === true} yes={yesText} no={noText} />
          <Switch
            size="small"
            disabled={!critical}
            checked={row[NOTIFICATION_CAPABILITY] === true}
            onChange={() => handleNotificationAccess(row)}
            inputProps={{ 'aria-label': `${t('notification_access', { defaultValue: 'Push Notifications' })}: ${nameOf(row)}` }}
          />
        </Box>
      ),
    },
  ];

  // The same CSV columns as before.
  const exportColumns = [
    { title: t('createdAt'), field: 'createdAt', type: 'datetime' },
    { title: t('first_name'), field: 'firstName' },
    { title: t('last_name'), field: 'lastName' },
    { title: t('mobile'), value: (row) => (critical ? row.mobile || '' : hiddenText) },
    { title: t('email'), value: (row) => (critical ? row.email || '' : hiddenText) },
    { title: t('user_type'), value: (row) => roleLabel(row.usertype) },
    { title: t('profile_image'), field: 'profile_image' },
    { title: t('account_approve'), value: (row) => (row.approved ? yesText : noText) },
    { title: t('notification_access', { defaultValue: 'Push Notifications' }), value: (row) => (row[NOTIFICATION_CAPABILITY] === true ? yesText : noText) },
  ];

  // Name and role always; phone and email only when they may be shown.
  const searchFn = (row, q, ql) => {
    if (nameOf(row).toLowerCase().includes(ql)) return true;
    if (String(roleLabel(row.usertype)).toLowerCase().includes(ql)) return true;
    if (!critical) return false;
    if (phoneMatches(row.mobile, q)) return true;
    return String(row.email || '').toLowerCase().includes(ql);
  };

  const actions = critical
    ? [
        { key: 'edit', label: t('edit'), icon: <EditOutlinedIcon />, inline: true, onClick: (row) => setEditing({ row }) },
        { key: 'delete', label: t('delete_user'), icon: <DeleteOutlineRoundedIcon />, inline: true, destructive: true, onClick: handleDeleteUser },
      ]
    : [];

  const addLabel = L('staff_add', 'Add staff member');
  const listTitle = t('internal_staff_title', { defaultValue: 'Internal Staff Management' });
  const isEdit = !!(editing && editing.row);
  const required = L('form_required', 'This field is required');

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PageHeader primaryAction={{ label: addLabel, icon: <PersonAddAltRoundedIcon />, onClick: () => setEditing({}), show: critical }} />
      <DataTable
        title={listTitle}
        rows={data}
        columns={columns}
        loading={!staticusers && !listFailed}
        error={listFailed ? { title: L('staff_load_failed', 'Could not load staff.'), onRetry: () => dispatch(fetchUsersOnce()) } : null}
        countText={({ shown, total, query, loading }) =>
          loading
            ? L('loading', 'Loading…')
            : query
              ? L('table_count_shown', '{{shown}} of {{total}}', { shown, total })
              : t('staff_count', {
                  count: total,
                  defaultValue_one: '{{count}} staff member',
                  defaultValue_other: '{{count}} staff members',
                  defaultValue: '{{count}} staff members',
                })
        }
        searchFn={searchFn}
        resortKey={critical ? 'private' : 'demo'}
        searchPlaceholder={critical ? L('drivers_search_hint', 'Search name, phone or email') : L('customers_search_name', 'Search by name')}
        rowLabel={nameOf}
        actions={actions}
        page={page}
        onPageChange={setPage}
        prefsKey="internal_staff"
        stateKey="internal_staff"
        exportCsv={critical}
        exportColumns={exportColumns}
        exportFileName="internal_staff"
        exportTitle={listTitle}
        emptyState={({ query }) =>
          query
            ? null
            : {
                icon: <BadgeOutlinedIcon />,
                title: L('staff_empty', 'No staff accounts yet'),
                body: L('staff_empty_body', 'Marketing, call-centre, finance and scout accounts appear here.'),
                actionLabel: critical ? addLabel : undefined,
                onAction: critical ? () => setEditing({}) : undefined,
              }
        }
        minWidth={1100}
      />
      <RecordDialog
        open={!!editing}
        mode={isEdit ? 'edit' : 'add'}
        title={isEdit ? L('staff_edit', 'Edit staff member') : addLabel}
        fields={[
          { name: 'firstName', label: t('first_name'), width: 'half', autoFocus: true },
          { name: 'lastName', label: t('last_name'), width: 'half' },
          // Only when adding (as before); stored as +251…
          { name: 'mobile', label: t('mobile'), type: 'phone', editable: 'onAdd', parse: toEthiopianE164 },
          { name: 'email', label: t('email'), type: 'email', editable: 'onAdd' },
          {
            name: 'usertype',
            label: t('user_type'),
            type: 'select',
            required: true,
            requiredMessage: required,
            defaultValue: 'callcenteragent',
            options: availableUserTypes.map((type) => ({ value: type, label: roleLabel(type) })),
          },
          { name: 'approved', label: calmCaps(t('account_approve')), type: 'switch' },
          { name: NOTIFICATION_CAPABILITY, label: t('notification_access', { defaultValue: 'Push Notifications' }), type: 'switch' },
        ]}
        // A new row starts as material-table started it.
        initialValues={isEdit ? editing.row : { firstName: '', lastName: '', approved: true, [NOTIFICATION_CAPABILITY]: false }}
        onSubmit={handleSave}
        onClose={() => setEditing(null)}
      />
    </Box>
  );
}
